import { PRICING_CONFIG } from './config';
import { getManualReasons, validateRoom } from './eligibility';
import { calculateGeometry } from './geometry';
import type { PricingConfig, QuoteInput, QuoteResult, RoomBreakdown, RoomInput } from './types';

export function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function calculateRoom(room: RoomInput, config: PricingConfig): RoomBreakdown {
  const geometry = calculateGeometry(room);
  const surfaceSubtotal =
    (room.paintWalls ? geometry.wallArea * config.rates.walls : 0) +
    (room.paintCeiling ? geometry.ceilingArea * config.rates.ceiling : 0) +
    (room.paintSkirting ? geometry.skirtingLength * config.rates.skirting : 0) +
    room.paintedDoors * config.rates.door +
    room.paintedRadiators * config.rates.radiator;

  const prepSubtotal =
    (room.wallpaperRemoval ? geometry.wallArea * config.rates.wallpaperRemoval : 0) +
    (room.stainBlocking ? geometry.wallArea * config.rates.stainBlocking : 0);

  const base = surfaceSubtotal + prepSubtotal;
  const conditionMultiplier = room.condition === 'specialist' ? 1 : config.conditionMultipliers[room.condition];
  const adjusted =
    base *
    conditionMultiplier *
    config.colourMultipliers[room.colourChange] *
    config.materialMultipliers[room.materialStandard] *
    (room.highCeiling || room.height > 2.8 ? config.additionalMultipliers.highCeiling : 1) *
    (room.difficultAccess ? config.additionalMultipliers.difficultAccess : 1) *
    (room.furnished ? config.additionalMultipliers.furnished : 1);

  return {
    roomId: room.id,
    geometry,
    surfaceSubtotal,
    prepSubtotal,
    base,
    adjusted
  };
}

export function calculateDecoratingQuote(input: QuoteInput, pricingConfig = PRICING_CONFIG[input.market]): QuoteResult {
  const invalidRooms = input.rooms.flatMap((room) => validateRoom(room).map((field) => `${room.id}:${field}`));
  if (invalidRooms.length > 0) {
    throw new RangeError(`Invalid quote input: ${invalidRooms.join(', ')}`);
  }

  const rooms = input.rooms.map((room) => calculateRoom(room, pricingConfig));
  const subtotal = rooms.reduce((sum, room) => sum + room.adjusted, 0);
  const regionalSubtotal = subtotal * pricingConfig.regionMultipliers[input.region];
  const travelCharge = input.travel === 'extended' ? pricingConfig.travelCharge : 0;
  const manualReasons = getManualReasons(input);

  if (manualReasons.length > 0) {
    return {
      status: 'manual',
      market: input.market,
      currency: pricingConfig.currency,
      rooms,
      subtotal,
      regionalSubtotal,
      travelCharge,
      centralEstimate: null,
      lowEstimate: null,
      highEstimate: null,
      manualReasons
    };
  }

  const centralEstimate = Math.max(pricingConfig.minimumCharge, regionalSubtotal + travelCharge);
  const lowEstimate = roundToStep(centralEstimate * pricingConfig.range.low, pricingConfig.roundingStep);
  const highEstimate = roundToStep(centralEstimate * pricingConfig.range.high, pricingConfig.roundingStep);

  return {
    status: 'estimate',
    market: input.market,
    currency: pricingConfig.currency,
    rooms,
    subtotal,
    regionalSubtotal,
    travelCharge,
    centralEstimate,
    lowEstimate,
    highEstimate,
    manualReasons: []
  };
}
